import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { RootState } from '../store';
import { ROUTES } from '../../routes/paths';
import { logOut, selectCurrentToken } from '../slices/authSlice';
import { setCompanyId, setLoginAllRoles, setLoginRole } from '../slices/companySlice';
import { setusersdata } from '../slices/signatoriesSlice';
import { useGetEmailByIndividualIdQuery } from '../api/usersApiHandler';
import type { LayoutRouteContext } from '../../features/layout/Layout';
import { apiURL } from '../../url/url';

const INACTIVITY_TIMEOUT = 10 * 60 * 1000;
const LAST_ACTIVITY_KEY = 'lastUserActivityAt';
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

const getTokenExpiry = (token: string): number | null => {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : null;
  } catch {
    return null;
  }
};

const RequireAuth = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const outletContext = useOutletContext<LayoutRouteContext | undefined>();
  const tokenFromRedux = useSelector(selectCurrentToken);
  const tokenFromSession = sessionStorage.getItem('token');
  const token = tokenFromRedux || tokenFromSession;
  const tokenExpiry = token ? getTokenExpiry(token) : null;
  const now = Date.now();
  const storedActivityValue = sessionStorage.getItem(LAST_ACTIVITY_KEY);
  const storedActivity = storedActivityValue === null ? null : Number(storedActivityValue);
  const hasInvalidActivity = storedActivityValue !== null && (!Number.isFinite(storedActivity) || storedActivity === null || storedActivity <= 0 || storedActivity > now);
  const hasExpiredInactivity = Boolean(token) && (hasInvalidActivity || (storedActivity !== null && now - storedActivity >= INACTIVITY_TIMEOUT));
  const isSessionValid =  Boolean(token) &&  tokenExpiry !== null &&  tokenExpiry > now &&  !hasExpiredInactivity;
  const lastActivityRef = useRef(0);
  const lastActivityPersistedRef = useRef(0);
  const CompanyStore = useSelector((state: RootState) => state.company.companyId);
  // const loginCurrentRole =  useSelector((state: RootState) =>    state.company.loginRole) || sessionStorage.getItem('Role');
  // Email from session
  const emailId = sessionStorage.getItem('LoggedUserEmail') || '';
  // User API
  const { data: UsersData } =
    useGetEmailByIndividualIdQuery({
      primaryEmail: emailId,
    },
      {
        skip: !emailId || !isSessionValid,
      }
    );

  const expireSession = useCallback(() => {
    const loginType = sessionStorage.getItem('loginType');
    const email = sessionStorage.getItem('LoggedUserEmail') ?? '';

    dispatch(setLoginRole(''));
    dispatch(setLoginAllRoles([]));
    dispatch(logOut());
    sessionStorage.clear();
    localStorage.removeItem('redirectAfterLogin');

    if (loginType && loginType !== 'Identity') {
      window.location.assign(`${apiURL}/identity/azuread/logout?email=${encodeURIComponent(email)}`);
      return;
    }

    navigate(ROUTES.login, { replace: true });
  }, [dispatch, navigate]);

  useEffect(() => {
    if (!token) {
      lastActivityRef.current = 0;
      lastActivityPersistedRef.current = 0;
      return;
    }

    const activityTime = Date.now();
    const savedActivityValue = sessionStorage.getItem(LAST_ACTIVITY_KEY);
    const savedActivity = savedActivityValue === null ? null : Number(savedActivityValue);
    const initialActivity = savedActivity ?? activityTime;
    lastActivityRef.current = initialActivity;
    lastActivityPersistedRef.current = initialActivity;

    const accessTokenExpiry = getTokenExpiry(token);
    if (
      accessTokenExpiry === null ||
      accessTokenExpiry <= activityTime ||
      hasInvalidActivity ||
      activityTime - initialActivity >= INACTIVITY_TIMEOUT
    ) {
      expireSession();
      return;
    }

    sessionStorage.setItem(LAST_ACTIVITY_KEY, String(initialActivity));

    let timeoutId: ReturnType<typeof setTimeout>;
    let isExpired = false;
    const endSession = () => {
      if (isExpired) {
        return;
      }
      isExpired = true;
      expireSession();
    };

    const scheduleExpiration = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const currentTime = Date.now();
        if (
          accessTokenExpiry <= currentTime ||
          currentTime - lastActivityRef.current >= INACTIVITY_TIMEOUT
        ) {
          endSession();
        } else {
          scheduleExpiration();
        }
      }, Math.max(
        0,
        Math.min(
          INACTIVITY_TIMEOUT - (Date.now() - lastActivityRef.current),
          accessTokenExpiry - Date.now(),
        ),
      ));
    };

    const checkExpiration = () => {
      const currentTime = Date.now();
      if (
        accessTokenExpiry <= currentTime ||
        currentTime - lastActivityRef.current >= INACTIVITY_TIMEOUT
      ) {
        endSession();
      } else {
        scheduleExpiration();
      }
    };

    const recordActivity = () => {
      const activityTime = Date.now();
      if (
        accessTokenExpiry <= activityTime ||
        activityTime - lastActivityRef.current >= INACTIVITY_TIMEOUT
      ) {
        endSession();
        return;
      }

      lastActivityRef.current = activityTime;
      if (activityTime - lastActivityPersistedRef.current >= 1000) {
        sessionStorage.setItem(LAST_ACTIVITY_KEY, String(activityTime));
        lastActivityPersistedRef.current = activityTime;
      }
      scheduleExpiration();
    };

    ACTIVITY_EVENTS.forEach(eventName => window.addEventListener(eventName, recordActivity, { passive: true }));
    window.addEventListener('focus', checkExpiration);
    document.addEventListener('visibilitychange', checkExpiration);
    scheduleExpiration();

    return () => {
      clearTimeout(timeoutId);
      ACTIVITY_EVENTS.forEach(eventName => window.removeEventListener(eventName, recordActivity));
      window.removeEventListener('focus', checkExpiration);
      document.removeEventListener('visibilitychange', checkExpiration);
    };
  }, [expireSession, hasInvalidActivity, token]);

  // Store CompanyId
  useEffect(() => {
    if (UsersData?.data?.length > 0) {
      sessionStorage.setItem('CompanyId', UsersData?.data?.[0]?.organizationId || '00000000-0000-0000-0000-000000000000');
    }
  }, [UsersData]);

  // Store User Data
  useEffect(() => {
    if (UsersData?.data?.length > 0 && CompanyStore == null) {
      dispatch(setusersdata(UsersData.data[0]));
    }
  }, [UsersData, CompanyStore, dispatch]);

  // Store LoggedUserId
  useEffect(() => {
    if (UsersData?.data?.length > 0) {
      const userId = UsersData.data[0]?.id;
      if (userId) {
        sessionStorage.setItem('LoggedUserId', userId);
      }
    }
  }, [UsersData]);

  // Set CompanyId Redux
  useEffect(() => {
    if (UsersData?.data?.length > 0) {
      if (CompanyStore === null || CompanyStore === undefined || Array.isArray(CompanyStore)) {
        dispatch(setCompanyId(UsersData?.data?.[0]?.organizationId || '00000000-0000-0000-0000-000000000000')
        );
      }
    }
  }, [CompanyStore, UsersData, dispatch,
  ]);

  return isSessionValid
    ? <Outlet context={outletContext} />
    : <Navigate to={ROUTES.login} state={{ from: location }} replace />;
};

export default RequireAuth;